import { useCallback } from "react";

import App from "byzantine/src/App";
import { entities } from "byzantine";

import type { SidebarRootItem } from "../sidebar.types";

/**
 * Hook that returns a builder for top-level NAF sidebar items.
 *
 * Internalizes `useNAFItems`. When a SidebarMenuItem_Resolved has
 * a `nafClientId`, this builder looks it up in the NAF app items
 * and returns a SidebarRootItem with `nafApp` set. Clicking it
 * triggers AppAuthorize instead of navigation.
 *
 * Returns null if the NAF app is not found (item not rendered).
 */
export const useTopLevelNAFMenuItem = () => {
  const appItems = entities.institution.useNAFItems();

  return useCallback(
    (menuItem: API.SidebarMenuItem_Resolved): SidebarRootItem | null => {
      if (!menuItem.nafClientId) return null;

      const matchedEntry = Object.values(appItems).find(
        ({ app }) => app && app.client_id === menuItem.nafClientId
      );

      if (!matchedEntry?.app) return null;

      try {
        const deserializedApp = App.deserialize(matchedEntry.app);
        return {
          ...menuItem,
          icon: deserializedApp.image || menuItem.icon,
          menuOptions: undefined,
          nafApp: deserializedApp,
        };
      } catch {
        return null;
      }
    },
    [appItems]
  );
};
